import { useState, useEffect } from "react";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState } from "@/components/DataState";
import { num, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Boxes, Cpu, Package, AlertTriangle, Mail, Save, Plus, X, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isGestor } from "@/lib/permissions";
import { StockProductDialog } from "@/components/StockProductDialog";

const iconOf = (p) => (p.legacy_key === "bobinas" ? Boxes : p.legacy_key === "chips" ? Cpu : Package);

function StockGauge({ p, gestor, onEdit, onDelete }) {
  const Icon = iconOf(p);
  const { quantity: qty, min_quantity: min, low } = p;
  const pct = min > 0 ? Math.min(100, Math.round((qty / (min * 2)) * 100)) : 100;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`stock-card-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className={`h-5 w-5 shrink-0 ${low ? "text-rose-400" : "text-sky-400"}`} />
          <h3 className="truncate text-base font-medium text-slate-200">{p.name}</h3>
        </div>
        <div className="flex items-center gap-1">
          {low && (
            <span className="flex items-center gap-1 rounded bg-rose-500/15 px-2 py-0.5 text-xs font-bold text-rose-300">
              <AlertTriangle className="h-3 w-3" /> Baixo
            </span>
          )}
          {gestor && (
            <>
              <button onClick={() => onEdit(p)} title="Editar" className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100" data-testid={`stock-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => onDelete(p)} title="Apagar" className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-rose-400" data-testid={`stock-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
            </>
          )}
        </div>
      </div>
      <p className={`mt-3 font-mono text-4xl font-extrabold ${low ? "text-rose-400" : "text-slate-50"}`} data-testid={`stock-qty-display-${p.id}`}>{num(qty)} <span className="text-base font-medium text-slate-500">{p.unit}</span></p>
      <p className="text-xs text-slate-500">Mínimo: {num(min)}</p>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-800">
        <div className={`h-full rounded-full ${low ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function NumGrid({ products, values, setValues, prefix, testKey }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {products.map((p) => (
        <div key={p.id} className="space-y-2">
          <Label className="text-slate-300">{prefix}{p.name}</Label>
          <Input type="number" min="0" value={values[p.id] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [p.id]: e.target.value }))}
            data-testid={`stock-${testKey}-${p.id}`} className="border-slate-700 bg-slate-950 text-slate-100" />
        </div>
      ))}
    </div>
  );
}

export default function Estoque() {
  const { data, loading, error, refetch } = useFetch("/stock");
  const { user } = useAuth();
  const gestor = isGestor(user);
  const [qty, setQty] = useState({});
  const [mins, setMins] = useState({});
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    if (data) {
      const q = {}, m = {};
      (data.products || []).forEach((p) => { q[p.id] = String(p.quantity ?? ""); m[p.id] = String(p.min_quantity ?? ""); });
      setQty(q); setMins(m);
      setEmails(data.alert_emails || []);
    }
  }, [data]);

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const products = data.products || [];

  const call = async (fn, key, msg = "Estoque atualizado") => {
    setBusy(key);
    try {
      await fn();
      toast.success(msg);
      await refetch();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const toInt = (v) => Math.max(0, parseInt(v, 10) || 0);
  const saveMinimums = () =>
    call(() => api.put("/admin/stock/minimums", { items: products.map((p) => ({ id: p.id, min_quantity: toInt(mins[p.id]) })) }), "min");
  const saveQty = () =>
    call(() => api.put("/funcionario/stock", { items: products.map((p) => ({ id: p.id, quantity: toInt(qty[p.id]) })) }), "qty");
  const deleteProduct = () => {
    const p = toDelete; setToDelete(null);
    call(() => api.delete(`/admin/stock/products/${p.id}`), "del", "Produto apagado");
  };

  const saveEmails = (list) =>
    call(() => api.put("/admin/stock/alert-emails", { emails: list }), "emails");
  const addEmail = () => {
    const e = newEmail.trim();
    if (!e) return;
    const list = [...emails, e];
    setEmails(list); setNewEmail("");
    saveEmails(list);
  };
  const removeEmail = (e) => {
    const list = emails.filter((x) => x !== e);
    setEmails(list);
    saveEmails(list);
  };

  return (
    <div className="space-y-6" data-testid="estoque">
      {gestor && (
        <div className="flex justify-end">
          <Button onClick={() => setEditing({})} className="bg-orange-500 text-white hover:bg-orange-600" data-testid="stock-add-product">
            <Plus className="h-4 w-4" /> Produto
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {products.map((p) => <StockGauge key={p.id} p={p} gestor={gestor} onEdit={setEditing} onDelete={setToDelete} />)}
        {products.length === 0 && <p className="text-sm text-slate-500">Nenhum produto cadastrado.</p>}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h3 className="mb-4 text-base font-medium text-slate-200">Atualizar quantidades</h3>
          <NumGrid products={products} values={qty} setValues={setQty} prefix="" testKey="qty" />
          <Button onClick={saveQty} disabled={busy === "qty"} data-testid="stock-save-qty"
            className="mt-4 bg-orange-500 text-white hover:bg-orange-600">
            <Save className="h-4 w-4" /> Salvar quantidades
          </Button>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h3 className="mb-4 text-base font-medium text-slate-200">Limites mínimos (alerta)</h3>
          <NumGrid products={products} values={mins} setValues={setMins} prefix="Mín. " testKey="min" />
          <Button onClick={saveMinimums} disabled={busy === "min"} data-testid="stock-save-min"
            className="mt-4 bg-orange-500 text-white hover:bg-orange-600">
            <Save className="h-4 w-4" /> Salvar mínimos
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <div className="mb-4 flex items-center gap-2">
          <Mail className="h-4 w-4 text-orange-400" />
          <h3 className="text-base font-medium text-slate-200">E-mails de alerta de estoque baixo</h3>
        </div>
        <div className="flex gap-2">
          <Input type="email" placeholder="email@empresa.com" value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
            data-testid="stock-email-input" className="border-slate-700 bg-slate-950 text-slate-100" />
          <Button onClick={addEmail} data-testid="stock-email-add" className="bg-orange-500 text-white hover:bg-orange-600">
            <Plus className="h-4 w-4" /> Adicionar
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {emails.length === 0 && <p className="text-sm text-slate-500">Nenhum e-mail cadastrado.</p>}
          {emails.map((e) => (
            <span key={e} className="flex items-center gap-2 rounded-full bg-slate-800 py-1 pl-3 pr-2 text-sm text-slate-200">
              {e}
              <button onClick={() => removeEmail(e)} className="rounded-full p-0.5 text-slate-500 hover:bg-slate-700 hover:text-rose-300"
                data-testid={`stock-email-remove-${e}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
        {data.updated_at && (
          <p className="mt-4 text-xs text-slate-600">
            Última atualização: {fmtDateTime(data.updated_at)}
            {data.updated_by_name ? ` • ${data.updated_by_name}` : ""}
          </p>
        )}
      </div>

      {gestor && <StockProductDialog product={editing} onClose={() => setEditing(null)} onSaved={refetch} />}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-slate-800 bg-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Apagar produto?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {toDelete?.name} será removido do estoque. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300" data-testid="stock-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteProduct} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="stock-delete-confirm">Apagar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
